import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import Paper from 'material-ui/Paper';
import { Typography, IconButton, Box, Tooltip } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import PageContent from '../../components/PageStructure/PageContent/PageContent';
import PageHeader from '../../components/PageStructure/PageHeader/PageHeader';
import InnerContent from '../../components/PageStructure/InnerContent/InnerContent';
import ReferralDetails from '../../components/Referrals/ReferralDetails/ReferralDetails';
import Spinner from '../../components/common/Spinner/Spinner';
import globals from '../../globals/communityGlobals';
import { doubleActionPrevent, formatNhs } from '../../utils/helpers';
import { Address } from '../../propTypes/entities/address';
import { paperStyle } from '../../theme/AllocateBaseTheme';
import { getFilterOptionsByReferralId } from '../../actions/areas/planner';

const locale = globals.getArea('referrals');

class ReferralView extends React.Component {
  state = {
    isReferralLoading: true,
    isFilterOptionsLoading: true,
    filterOptions: {},
  };

  componentDidMount = () => {
    const { match, getReferral } = this.props;
    getReferral(match.params.referralId).finally(() => {
      this.setState({ isReferralLoading: false });
    });
    getFilterOptionsByReferralId(
      this.props.auth,
      { referralId: match.params.referralId },
      false,
      this.setFilterOptions,
    );
  };

  setFilterOptions = response => {
    if (response) {
      this.setState({ filterOptions: response, isFilterOptionsLoading: false });
    }
  };

  handleGoBack = () => {
    this.props.goBack();
  };

  handleNavigate = url => () => {
    this.props.navigate(url);
  };

  handleSave = doubleActionPrevent.bind(this)(
    'ReferralEdit',
    async model => {
      const { updateReferral, match } = this.props;

      await updateReferral({
        id: match.params.referralId,
        dateReceived: moment(model.referralReceived).format(globals.dateFormats.date),
        priorityId: model.referralPriorityId,
        sourceId: model.referralSourceId,
        serviceId: model.referralServiceId,
        orgUnitIds: [model.allocatedToTeams],
        locationTypeId: model.locationTypeId,
        referenceCodeId: model.referenceCodeId,
        pasReferralId: model.pasReferralId,
      });
    },
    () => {
      const { navigate, referral } = this.props;
      navigate(`/Patients/${referral.patient.id}/Referrals`);
    },
  );

  handleSyncTreatments = doubleActionPrevent.bind(this)('TreatmentsSync', async () => {
    const { syncTreatments, referral } = this.props;

    await syncTreatments({
      externalReferralId: referral.externalServiceReferralId,
      referralId: referral.id,
      patientId: referral.patient.id,
    });
  });

  render() {
    const {
      pageTitle,
      isFetching,
      failureMessage,
      referral,
      match,
      navigate,
      canAddTreatments,
      canEditReferrals,
      canEditTreatments,
      referralTreatments,
      enableIntegrationMode,
      externalProviderType,
      isFetchingComplete,
      areTreatmentsFetching,
      messages,
      resetSyncStatusMsg,
    } = this.props;
    const { isFilterOptionsLoading, isReferralLoading } = this.state;
    const { patient } = referral || {};
    const isClosed = referral && referral.status === 'closed';
    let content = null;
    const isImported = enableIntegrationMode && referral && referral.isExternalProviderSource;
    if (!referral && failureMessage.length > 0) {
      content = <Paper style={paperStyle}>{failureMessage}</Paper>;
    } else if (isFilterOptionsLoading || isReferralLoading || isFetching) {
      content = (
        <Paper style={paperStyle}>
          <Spinner />
        </Paper>
      );
    } else {
      content = (
        <Paper style={paperStyle}>
          <ReferralDetails
            showTreatments
            handleSave={this.handleSave}
            handleSyncTreatments={this.handleSyncTreatments}
            resetSyncStatusMsg={resetSyncStatusMsg}
            referralId={referral ? referral.id : null}
            referral={referral}
            navigate={navigate}
            match={match}
            readOnly={isClosed}
            showClose={!isClosed}
            canAddTreatments={canAddTreatments}
            canEditTreatments={canEditTreatments}
            canEdit={canEditReferrals}
            isImported={isImported}
            referralTreatments={
              referralTreatments && referralTreatments.searchResults ? referralTreatments.searchResults.items : []
            }
            externalProviderType={externalProviderType}
            isFetchingComplete={isFetchingComplete}
            isSyncFetching={areTreatmentsFetching}
            messages={messages}
            filterOptions={this.state.filterOptions}
          />
        </Paper>
      );
    }
    return (
      <PageContent pageTitle={pageTitle}>
        <div className="flex-page-content content-wrapper">
          <PageHeader>
            <Box className="title-section" display="flex" alignItems="center" px={2} py={1}>
              <Box>
                <Tooltip title={locale.getMsg('back')}>
                  <IconButton
                    className="back-btn"
                    aria-label="back"
                    size="small"
                    color="primary"
                    onClick={this.handleGoBack}
                  >
                    <ArrowBackIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box flexGrow={1}>
                <Typography variant="h3">{locale.getMsg('viewPageHeader')}</Typography>
                <Typography variant="h6">
                  {patient && `${patient.patientFullName} - NHS  Number: ${formatNhs(patient.nhsNumber)}`}
                </Typography>
              </Box>
            </Box>
          </PageHeader>
          <InnerContent>
            <div className="full-page">{content}</div>
          </InnerContent>
        </div>
      </PageContent>
    );
  }
}

ReferralView.defaultProps = {
  failureMessage: '',
  referral: undefined,
};

ReferralView.propTypes = {
  updateReferral: PropTypes.func.isRequired,
  isFetching: PropTypes.bool.isRequired,
  navigate: PropTypes.func.isRequired,
  failureMessage: PropTypes.string,
  referral: PropTypes.shape({
    dateReceived: PropTypes.string,
    id: PropTypes.number,
    locationType: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    orgUnits: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      }),
    ),
    patient: PropTypes.shape({
      address: PropTypes.shape(Address),
      patientFullName: PropTypes.string,
    }),
    referenceCode: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    referralPriority: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    referralSource: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    referralService: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    treatments: PropTypes.arrayOf(
      PropTypes.shape({
        address: PropTypes.shape(Address),
        id: PropTypes.number,
        patientId: PropTypes.string,
      }),
    ),
  }),
  pageTitle: PropTypes.string.isRequired,
  goBack: PropTypes.func.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      referralId: PropTypes.string,
    }),
  }).isRequired,
  getReferral: PropTypes.func.isRequired,
  canAddTreatments: PropTypes.bool.isRequired,
  canEditReferrals: PropTypes.bool.isRequired,
  canEditTreatments: PropTypes.bool.isRequired,
};

export default ReferralView;
